import _ from "lodash";
import moment from "moment";
import React, { Component } from "react";
import { Progress } from "reactstrap";
import ReactTable from "react-table";
import {
  ToggableDropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
} from "./Dropdown";
import Pagination from "../../views/Pagination";
import TextFilter from "./TextFilter";
import DateFilter from "./DateFilter";
import FlagFilter from "./FlagFilter";
import ToggleFilter from "./ToggleFilter";
import * as util from "../../utils/helpers";

import IconYes from "../../../assets/images/icon_yes.svg";
import IconNo from "../../../assets/images/icon_no.svg";
import FilterDisabledImage from "../../../assets/images/filtergray.svg";
import FilterEnabledImage from "../../../assets/images/filter.svg";
import FilterCancelImage from "../../../assets/images/cancel.png";

const DEFAULT_TABLE_STATE = {
  showFilters: false,
  page: 0,
  pageSize: 5,
  filters: [],
  sorters: [],
  activeColumn: null,
};

const persistedTablesState = JSON.parse(window.localStorage.getItem("tables_states") || "{}");

const filterText = (filter, row) =>
  (!filter || !filter.value) ||
  (filter.value === "-" && !row.value) ||
  util.fuzzySearch(filter, row);

const filterDate = (filter, row) => {
  if (!filter || !filter.value) {
    return true;
  }

  const from = moment(filter.value.from).utc();
  const to = moment(filter.value.to).utc();
  return moment.utc(row[filter.id]).isBetween(from, to);
};

const filterFlag = (filter, row) =>
  !filter ||
  filter.value === null ||
  filter.value === undefined ||
  (filter.value === true || filter.value === "true") ===
  (row[filter.id] === true || row[filter.id] === "true");

class Table extends Component {
  constructor(props) {
    super(props);

    persistedTablesState[props.id] = persistedTablesState[props.id] || _.clone(DEFAULT_TABLE_STATE);

    if (!persistedTablesState[props.id].activeColumn) {
      const sorters = props.defaultSorters ? props.defaultSorters : [{
        id: props.columns[0].accessor,
        desc: false
      }];

      persistedTablesState[props.id].activeColumn = sorters[0].id;
      persistedTablesState[props.id].sorters = sorters;
    }

    this.state = {
      items: this.props.data || [],
      sorters: persistedTablesState[props.id].sorters,
      activeColumn: persistedTablesState[props.id].activeColumn,
      showFilters: persistedTablesState[props.id].showFilters,
      hasMoreItems: false,
    };

    this.table = React.createRef();

    this.fetch = _.debounce(this.fetch.bind(this), this.props.onFetch ? 10 : 700, { trailing: true });
    this.persistState = this.persistState.bind(this);
    this.onWindowResize = this.onWindowResize.bind(this);
  }

  componentDidMount() {
    persistedTablesState[this.props.id] = persistedTablesState[this.props.id] || _.clone(DEFAULT_TABLE_STATE);

    const { page, pageSize, filters, sorters } = persistedTablesState[this.props.id];

    this.table.current.setState({
      page,
      pageSize,
      filtered: filters,
      sorters: sorters
    });

    window.addEventListener("resize", this.onWindowResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.onWindowResize);
  }

  componentDidUpdate() {
    this.persistState();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!nextProps.data) {
      return;
    }

    if (
      !this.props.items ||
      this.props.items.length !== nextProps.data.length ||
      _.differenceWith(this.props.data, nextProps.data, _.isEqual).length
    ) {
      this.setState({
        items: nextProps.data,
        page: 0,
      });
    }
  }

  onWindowResize() {
    this.table.current.setState({ page: 0 });
    this.persistState();
  }

  persistState(values = {}) {
    persistedTablesState[this.props.id] = {
      page: this.table.current.state.page,
      pageSize: this.table.current.state.pageSize,
      filters: this.table.current.state.filtered,
      sorters: this.state.sorters,
      activeColumn: this.state.activeColumn,
      showFilters: this.state.showFilters,
      ...values
    };

    window.localStorage.setItem("tables_states", JSON.stringify(persistedTablesState));
  }

  prepareColumns() {
    const { showFilters, sorters, activeColumn } = this.state;

    const toggleFilters = () => {
      this.persistState({ page: 0, filters: [] });
      this.table.current.setState({ page: 0, filtered: [] });
      this.table.current.fireFetchData();
      this.setState({ showFilters: !this.state.showFilters });
    };

    const DefaultColumnHeader = (name, id, className) => {
      const isActive = activeColumn === id;
      const sorter = _.find(sorters, { id: id });

      return (
        <div className={`table-head-outer ${className} ${isActive ? "blue-txt" : ""}`}>
          <p className="table-head">{name}</p>
          <i
            className={
              sorter && !sorter.desc ? "fa fa-caret-up" : "fa fa-caret-down"
            }
            aria-hidden="true"
          />
        </div>
      );
    };

    const FiltersColumnHeader = (
      <div
        className={showFilters ? "filter-outer" : "filter-hover"}
        onClick={() => toggleFilters()}
      >
        <img
          className="filter-icon"
          src={showFilters ? FilterEnabledImage : FilterDisabledImage}
        />
      </div>
    );

    const DateCell = (data) =>
      moment
        .utc(data.value)
        .local()
        .format(this.props.dateFormat || "MM/DD/YY hh:mm a");

    const FlagCell = (data) => (
      <div className="yesnoIcon">
        <i
          className={data.original[data.column.id] === "true" || data.original[data.column.id] ? 'fa fa-check' : 'fa fa-times'}
        />
      </div>
    );

    const ToggleCell = (data) => (
      <div className="yesnoIcon">
        <img
          className="tw-cursor-pointer hover:tw-opacity-90"
          onClick={() => this.props.onAction('toggle', { id: data.column.id, data: data.original })}
          src={data.original[data.column.id] === "true" || data.original[data.column.id] ? IconYes : IconNo}
        />
      </div>
    );

    const CheckCell = (data) => (
      <div className="yesnoIcon">
        <i
          onClick={() => this.props.onAction('checkbox', { id: data.column.id, data: data.original })}
          className={this.props.selected.indexOf(data.original.user_id) !== -1 ? 'fa fa-check' : 'fa fa-times'}
        />
      </div>
    );

    const FilterCell = (data) => {
      const visibleActions = this.props.actions.filter(action =>
        !action.isHidden || !action.isHidden(data.original)
      );

      return (
        <div className="filterdrop-outer">
          <ToggableDropdown className="filter-dropown">
            <DropdownToggle>
              <div className="more">
                <i className="fa fa-ellipsis-h" aria-hidden="true"></i>
              </div>
            </DropdownToggle>
            <DropdownMenu>
              {visibleActions.map((action) => (
                <DropdownItem
                  key={action.id}
                  onClick={() => this.props.onAction(action.id, data.original)}
                >
                  <div id="TooltipComponent">
                    <i className={`fa ${action.icon}`} aria-hidden="true" />
                    {action.description}
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </ToggableDropdown>
        </div>
      );
    };

    const columns = _.map(this.props.columns, (column) => ({
      Header: _.get(
        {
          text: DefaultColumnHeader(column.name, column.accessor),
          flag: DefaultColumnHeader(column.name, column.accessor, "yesno-head"),
          toggle: DefaultColumnHeader(column.name, column.accessor, "yesno-head"),
          date: DefaultColumnHeader(column.name, column.accessor),
        },
        column.type
      ),
      Filter: _.get(
        {
          text: TextFilter,
          date: DateFilter,
          flag: FlagFilter,
          toggle: ToggleFilter,
        },
        column.type
      ),
      Cell:
        column.cell ||
        _.get(
          {
            date: DateCell,
            flag: FlagCell,
            toggle: ToggleCell,
            check: CheckCell,
          },
          column.type
        ),
      accessor: column.accessor,
      filterable: column.filterable && showFilters,
      filterMethod: this.props.onFetch ? () => true : _.get(
        {
          text: filterText,
          date: filterDate,
          flag: filterFlag,
          toggle: filterFlag,
        },
        column.type
      ),
    }));

    columns.push({
      Header: FiltersColumnHeader,
      Filter: (
        <button onClick={toggleFilters} className="cancel-btn">
          <img src={FilterCancelImage} />
        </button>
      ),
      Cell: this.props.actions ? FilterCell : undefined,
      filterable: showFilters,
      sortable: false,
    });

    return columns;
  }

  async fetch() {
    if (!this.props.onFetch) {
      return;
    }

    const info = persistedTablesState[this.props.id] || {
      showFilters: false,
      page: 0,
      pageSize: 5,
      filters: [],
      sorters: [],
      activeColumn: null,
    };

    const { items = [] } = await this.props.onFetch({
      page: info.page,
      pageSize: info.pageSize,
      filters: info.filters,
      sortBy: info.sorters[0].id,
      sortDirection: info.sorters[0].desc ? "desc" : "asc"
    });

    this.setState({
      items: _.take(items, info.pageSize),
      hasMoreItems: items.length > info.pageSize
    });

    this.table.current.setState({
      page: 0
    });
  }

  render() {
    const getPage = () => persistedTablesState[this.props.id].page;
    const getPageSize = () => persistedTablesState[this.props.id].pageSize;

    const setPage = (page) => {
      this.persistState({ page });
      this.table.current.setState({ page });
      this.table.current.fireFetchData();
    };

    const setPageSize = (pageSize) => {
      this.persistState({ page: 0, pageSize });
      this.table.current.setState({ page: 0, pageSize });
      this.table.current.fireFetchData();
    };

    const hasMoreItems = (page, pageSize) => {
      return this.props.onFetch
        ? this.state.hasMoreItems
        : this.state.items.length > ((page + 1) * pageSize);
    }

    const updateSorters = (sorters, column) => {
      this.table.current.setState({ page: 0 });
      this.persistState({ sorters, activeColumn: column.id });
      this.setState({ sorters, activeColumn: column.id });
    };

    const updateFilters = (filters) => {
      setPage(0);
    }

    return (
      <ReactTable
        ref={this.table}
        className="-striped -highlight site_table"
        manual={!!this.props.onFetch}
        PaginationComponent={Pagination}
        columns={this.prepareColumns()}
        data={this.state.items}
        minRows = {0}
        defaultPageSize={5}
        defaultSorted={this.state.sorters}
        getPaginationProps={() => ({
          getPage,
          setPage,
          getPageSize,
          setPageSize,
          hasMoreItems
        })}
        onFilteredChange={updateFilters}
        onSortedChange={updateSorters}
        onPageSizeChange={() => setPage(0)}
        onFetchData={this.fetch}
      />
    );
  }
}

export default Table;
